import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import mascot from "@/assets/mascot.png";
import { useLang } from "@/hooks/use-language";
import { listPublicCharacters, type CharacterRow } from "@/lib/characters.functions";
import { CharacterImage } from "@/components/CharacterImage";
import { translateTrait } from "@/lib/character-meta";

export const Route = createFileRoute("/characters")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/characters" },
      { title: "Characters | شخصيات جذوري — Jozori" },
      {
        name: "description",
        content:
          "تعرّف على شخصيات جذوري — جاد، لانا، جدّو سليم، ونوش. Meet the Jozori characters who guide children through stories and values.",
      },
      { property: "og:title", content: "Jozori Characters — شخصيات جذوري" },
      {
        property: "og:description",
        content:
          "Meet the recurring cast of Jozori — friends who teach values through every story and adventure.",
      },
      { property: "og:image", content: "https://jozori.com/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://jozori.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/characters" }],
  }),
  loader: () => listPublicCharacters(),
  component: CharactersRouteShell,
});

function CharactersRouteShell() {
  const { pathname } = useLocation();
  return pathname === "/characters" ? <CharactersIndex /> : <Outlet />;
}

function CharactersIndex() {
  const characters = Route.useLoaderData() as CharacterRow[];
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";

  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-clay/30 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display-en text-lg font-black">
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span>Jozori</span>
          </Link>
          <button
            onClick={toggle}
            className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
          >
            {isAr ? "English" : "العربية"}
          </button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/15"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-primary">
            {isAr ? "شخصيات جذوري" : "Jozori cast"}
          </p>
          <h1
            className={`mx-auto max-w-3xl text-4xl md:text-5xl font-black leading-tight ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {isAr ? "تعرّف على أصدقاء طفلك" : "Meet your child's friends"}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-foreground/70">
            {isAr
              ? "كل قصة على جذوري ترويها شخصيات يحبها الأطفال، تكبر معهم وتعلّمهم القيم بلطف."
              : "Every Jozori story is told by characters children love — friends who grow with them and teach values gently."}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        {characters.length === 0 ? (
          <p className="text-center text-foreground/60">
            {isAr ? "لا توجد شخصيات بعد." : "No characters yet."}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {characters.map((c) => (
              <Link
                key={c.code}
                to="/characters/$code"
                params={{ code: c.code }}
                className="group rounded-3xl border-2 border-clay/30 bg-card p-6 hover:-translate-y-1 transition-all"
                style={{ borderColor: `${c.color}33` }}
              >
                <div
                  className="size-20 rounded-2xl flex items-center justify-center text-3xl font-black mb-4 ring-4 ring-offset-2 ring-offset-card"
                  style={{
                    background: `linear-gradient(135deg, ${c.color}33, ${c.color}66)`,
                    color: c.color,
                    boxShadow: `0 8px 24px -8px ${c.color}66`,
                  }}
                >
                  {c.avatar_url || c.code ? (
                    <CharacterImage
                      code={c.code}
                      fallbackUrl={c.avatar_url}
                      alt={isAr ? c.name_ar : c.name_en}
                      className="size-full rounded-2xl object-cover"
                      sizes="80px"
                    />
                  ) : (
                    <span>{(isAr ? c.name_ar : c.name_en).slice(0, 1)}</span>
                  )}
                </div>
                <h2
                  className={`text-xl font-black ${isAr ? "font-display-ar" : "font-display-en"}`}
                  style={{ color: c.color }}
                >
                  {isAr ? c.name_ar : c.name_en}
                </h2>
                {(isAr ? c.bio_ar : c.bio_en) && (
                  <p className="mt-1 text-sm text-foreground/70 line-clamp-3">
                    {isAr ? c.bio_ar : c.bio_en}
                  </p>
                )}
                {c.traits.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.traits.slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                        style={{ background: `${c.color}22`, color: c.color }}
                      >
                        {translateTrait(t, isAr)}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
